import { html } from '@shared/utils/rendering/html';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useCachedFetch } from '../../../utils/hooks/use-cached-fetch';
import Dropdown from '@shared/components/dropdowns';
import Chip from '@shared/components/chips';

const periodStatusMap = {
    Open: { value: 3, text: 'Open', css: 'bg-blue' },
    Active: { value: 2, text: 'Active', css: 'bg-green' },
    Future: { value: 1, text: 'Future', css: 'bg-grey' },
    Past: { value: 0, text: 'Past', css: 'bg-orange' },
    Closed: { value: -1, text: 'Closed', css: 'bg-black' },
};

// Calculate period status from dates
const getPeriodStatus = (period) => {
    const now = Date.now();
    return new Date(period.endDate) < now
        ? period.valid
            ? periodStatusMap.Past
            : periodStatusMap.Closed
        : new Date(period.startDate) > now
          ? period.valid
              ? periodStatusMap.Open
              : periodStatusMap.Future
          : periodStatusMap.Active;
};

const periodChipMap = (period) => {
    // Store status info in the period object for transformer to use
    return {
        ...period,
        _periodStatus: getPeriodStatus(period),
    };
};

const PeriodsFilter = ({ field, params = {} }) => {
    const [options, setOptions] = useState([]);
    const autoSelectedRef = useRef(false);
    const { data: periods, isLoading, isSuccess } = useCachedFetch('/periods', params);

    useEffect(() => {
        if (!isLoading && isSuccess && periods?.length) {
            // Latest period first (most recent start date at the top of the dropdown).
            const sorted = [...periods].sort(
                (a, b) => new Date(b.startDate) - new Date(a.startDate)
            );
            setOptions(sorted.map(periodChipMap));
        }
    }, [periods]);

    // Default to the active period when nothing is selected yet (e.g. first visit or an
    // expired cache). A cached/restored selection takes precedence — the field.value guard
    // means we never override an existing choice.
    useEffect(() => {
        if (autoSelectedRef.current || field?.value || !options.length) return;
        const activePeriod = options.find(
            (o) => o._periodStatus?.text === periodStatusMap.Active.text
        );
        if (activePeriod && field?.setValue) {
            autoSelectedRef.current = true;
            field.setValue(activePeriod, field);
        }
    }, [options, field?.value]);

    return html` <${Dropdown}
        label=${{ text: 'Period' }}
        options=${options}
        field=${field}
        settings=${{
            multiple: false,
            type: 'input',
            transformer: (option) => {
                // Always recreate the display component to ensure it renders correctly
                if (option._periodStatus) {
                    return html`<div class="period-option">
                        <${Chip}
                            label=${{ text: option._periodStatus.text }}
                            css=${option._periodStatus.css + ' me-2'}
                            type="indicator-chip"
                        />
                        ${option.text}
                    </div>`;
                }
                return option.text;
            },
        }}
        disabled=${!options?.length}
    />`;
};

export default PeriodsFilter;
